// @ts-nocheck
import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import "../styles/TopNav.css";
import iconFeed from "../assets/icon-feed.png";
import iconTrades from "../assets/icon-trades.png";
import iconTradeExchange from "../assets/icon-trade-exchange.png";
import iconMessages from "../assets/icon-messages.png";
import iconEvents from "../assets/icon-events.png";
import iconProfile from "../assets/icon-profile.png";
import AccountMenu from "./AccountMenu";
import NavbarScannerModal from "./NavbarScannerModal";
import NotificationBadge from "./NotificationBadge";
import { useRealtime } from "../realtime/useRealtime";
import { RealtimeEvents } from "../realtime/events";
import { getToken, getUser } from "../auth/auth";
import { searchPublicUsers } from "../api/users";
import { isUsableAvatarUrl } from "../utils/avatar";
import { useRealtimeSocket } from "../realtime/useRealtime";
import { useAppDispatch } from "../store/hooks";
import { conversationsApi, useGetUnreadThreadsCountQuery } from "../store/api/conversationsApi";

export default function TopNav() {
  useRealtime(Boolean(getToken()));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const realtime = useRealtimeSocket();
  const userSearchRef = useRef(null);
  const searchInputRef = useRef(null);
  const scannerMenuRef = useRef(null);
  const scannerTriggerRef = useRef(null);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const [scannerMenuOpen, setScannerMenuOpen] = useState(false);
  const [scannerMenuStyle, setScannerMenuStyle] = useState({});
  const [scannerModalMode, setScannerModalMode] = useState(null);
  const [avatarLoadErrors, setAvatarLoadErrors] = useState({});
  const localUser = getUser();
  const currentUserId = String(localUser?.id || "");
  const { data: unreadThreadsData } = useGetUnreadThreadsCountQuery(undefined, {
    skip: !currentUserId,
    refetchOnReconnect: true,
    refetchOnMountOrArgChange: true,
  });
  const unreadThreadCount = currentUserId ? Math.max(0, Number(unreadThreadsData?.count ?? 0)) : 0;

  const tradeRouteActive =
    location.pathname === "/trades" ||
    location.pathname.startsWith("/trade-offer/") ||
    location.pathname.startsWith("/offer/");
  const postRouteActive =
    location.pathname === "/local-trades" ||
    location.pathname.startsWith("/post-trade") ||
    location.pathname.startsWith("/post-bounty") ||
    location.pathname.startsWith("/bounty-offer/");
  const profileRouteActive = location.pathname === "/profile" || location.pathname.startsWith("/settings");
  useEffect(() => {
    if (!currentUserId) return;

    const onUnreadThreadsUpdated = (payload) => {
      if (typeof payload?.count !== "number") return;
      dispatch(
        conversationsApi.util.upsertQueryData("getUnreadThreadsCount", undefined, {
          count: Math.max(0, Number(payload.count)),
        }),
      );
    };

    realtime.on(RealtimeEvents.ChatUnreadThreadsCountUpdated, onUnreadThreadsUpdated);
    return () => {
      realtime.off(RealtimeEvents.ChatUnreadThreadsCountUpdated, onUnreadThreadsUpdated);
    };
  }, [currentUserId, dispatch, realtime]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (debouncedQuery.length < 2) {
      setResults([]);
      setSearchLoading(false);
      setHighlightIndex(-1);
      setAvatarLoadErrors({});
      return;
    }

    let active = true;
    setSearchLoading(true);
    searchPublicUsers({ q: debouncedQuery, limit: 10 })
      .then((data) => {
        if (!active) return;
        setResults((data || []).slice(0, 8));
        setHighlightIndex(-1);
        setAvatarLoadErrors({});
      })
      .catch(() => {
        if (!active) return;
        setResults([]);
      })
      .finally(() => {
        if (active) {
          setSearchLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [debouncedQuery]);

  useEffect(() => {
    setSearchOpen(false);
    setHighlightIndex(-1);
    setScannerMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!userSearchRef.current) return;
      if (!userSearchRef.current.contains(event.target)) {
        setSearchOpen(false);
        setHighlightIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!scannerMenuOpen) return;
      const clickedTrigger = scannerTriggerRef.current?.contains(event.target);
      const clickedMenu = scannerMenuRef.current?.contains(event.target);
      if (!clickedTrigger && !clickedMenu) {
        setScannerMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setScannerMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [scannerMenuOpen]);

  useEffect(() => {
    if (!searchOpen || !searchInputRef.current) return;

    const updateDropdownPosition = () => {
      const rect = searchInputRef.current.getBoundingClientRect();
      const viewportPadding = 12;
      const width = Math.min(Math.max(rect.width, 260), window.innerWidth - viewportPadding * 2);
      const left = Math.max(
        viewportPadding,
        Math.min(rect.left, window.innerWidth - width - viewportPadding),
      );

      setDropdownStyle({
        top: rect.bottom + 6,
        left,
        width,
      });
    };

    updateDropdownPosition();
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);

    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
    };
  }, [searchOpen, query, debouncedQuery, results.length]);

  useEffect(() => {
    if (!scannerMenuOpen || !scannerTriggerRef.current) return;

    const updateScannerMenuPosition = () => {
      const rect = scannerTriggerRef.current.getBoundingClientRect();
      const viewportPadding = 12;
      const width = Math.min(220, window.innerWidth - viewportPadding * 2);
      const left = Math.max(
        viewportPadding,
        Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - viewportPadding),
      );

      setScannerMenuStyle({
        top: rect.bottom + 8,
        left,
        width,
      });
    };

    updateScannerMenuPosition();
    window.addEventListener("resize", updateScannerMenuPosition);
    window.addEventListener("scroll", updateScannerMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateScannerMenuPosition);
      window.removeEventListener("scroll", updateScannerMenuPosition, true);
    };
  }, [scannerMenuOpen]);

  const navigateToProfile = (username) => {
    if (!username) return;
    setSearchOpen(false);
    setHighlightIndex(-1);
    if (localUser?.username && String(localUser.username).toLowerCase() === String(username).toLowerCase()) {
      navigate("/profile");
      return;
    }
    navigate(`/u/${encodeURIComponent(username)}`);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Escape") {
      setSearchOpen(false);
      setHighlightIndex(-1);
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!searchOpen) setSearchOpen(true);
      setHighlightIndex((prev) => Math.min(prev + 1, results.length - 1));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, -1));
      return;
    }
    if (event.key === "Enter") {
      if (highlightIndex >= 0 && results[highlightIndex]) {
        event.preventDefault();
        navigateToProfile(results[highlightIndex].username);
      }
    }
  };

  const getResultInitial = (item) => {
    const source = item?.name || item?.username || "U";
    return String(source).trim().charAt(0).toUpperCase() || "U";
  };

  const launchScannerModal = (mode) => {
    setScannerMenuOpen(false);
    setScannerModalMode(mode);
  };

  return (
    <>
      <nav className="top-nav">
        <div className="top-nav-shell">
          <div className="top-nav-row top-nav-row--primary">
            <button
              type="button"
              className="top-nav-brand"
              onClick={() => navigate("/home")}
              aria-label="Go to Cardex home"
            >
              <span className="top-nav-brand-mark" aria-hidden="true">
                <span>CX</span>
              </span>
              <span className="top-nav-brand-copy">
                <span className="top-nav-brand-name">Cardex</span>
              </span>
            </button>

            <div className="top-nav-utilities">
              <div className="top-nav-user-search" ref={userSearchRef}>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    if (!searchOpen) setSearchOpen(true);
                  }}
                  onFocus={() => setSearchOpen(true)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search users"
                  aria-label="Search users"
                />
                {searchOpen && (
                  <div className="top-nav-user-search-dropdown" style={dropdownStyle}>
                    {query.trim().length < 2 ? (
                      <div className="search-state">Type at least 2 characters</div>
                    ) : searchLoading ? (
                      <div className="search-state">Searching…</div>
                    ) : results.length === 0 ? (
                      <div className="search-state">No users found</div>
                    ) : (
                      results.map((item, index) => (
                        <button
                          type="button"
                          key={`${item.id}-${item.username}`}
                          className={`search-result-item ${highlightIndex === index ? "active" : ""}`}
                          onMouseEnter={() => setHighlightIndex(index)}
                          onClick={() => navigateToProfile(item.username)}
                        >
                          <span className="search-result-avatar">
                            {isUsableAvatarUrl(item.avatar) && !avatarLoadErrors[item.id] ? (
                              <img
                                src={item.avatar}
                                alt={`${item.username || item.name || "User"} avatar`}
                                onError={() =>
                                  setAvatarLoadErrors((prev) => ({ ...prev, [item.id]: true }))
                                }
                              />
                            ) : (
                              <span className="search-result-avatar-fallback" aria-hidden="true">
                                {getResultInitial(item)}
                              </span>
                            )}
                          </span>
                          <span className="search-result-text">
                            <span className="search-result-name">@{item.username || "user"}</span>
                            <span className="search-result-meta">{item.name || "Cardex Trader"}</span>
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              <NotificationBadge />
              <AccountMenu onLaunchScanner={launchScannerModal} />
            </div>
          </div>

          <div className="top-nav-row top-nav-row--secondary">
            <div className="top-nav-links-scroll">
              <div className="top-nav-links">
                <NavLink
                  to="/home"
                  className={({ isActive }) => `top-nav-item nav-bounties ${isActive ? "active" : ""}`}
                >
                  <div className="top-nav-icon-wrapper">
                    <img src={iconFeed} alt="" className="top-nav-icon" />
                  </div>
                  <span className="top-nav-label">Bounties</span>
                </NavLink>

                <NavLink
                  to="/trades"
                  className={`top-nav-item nav-trades ${tradeRouteActive ? "active" : ""}`}
                >
                  <div className="top-nav-icon-wrapper">
                    <img src={iconTradeExchange} alt="" className="top-nav-icon" />
                  </div>
                  <span className="top-nav-label">Trades</span>
                </NavLink>

                <NavLink
                  to="/local-trades"
                  className={`top-nav-item nav-post ${postRouteActive ? "active" : ""}`}
                >
                  <div className="top-nav-icon-wrapper">
                    <img src={iconTrades} alt="" className="top-nav-icon" />
                  </div>
                  <span className="top-nav-label">Post</span>
                </NavLink>

                <button
                  ref={scannerTriggerRef}
                  type="button"
                  className={`top-nav-item top-nav-item-button nav-scanner ${scannerMenuOpen ? "active" : ""}`}
                  aria-haspopup="menu"
                  aria-expanded={scannerMenuOpen}
                  onClick={() => setScannerMenuOpen((value) => !value)}
                >
                  <div className="top-nav-icon-wrapper">
                    <span className="top-nav-icon-badge" aria-hidden="true">SC</span>
                  </div>
                  <span className="top-nav-label">Scanner</span>
                </button>

                <NavLink
                  to="/messages"
                  className={({ isActive }) => `top-nav-item nav-messages ${isActive ? "active" : ""}`}
                >
                  <div className="top-nav-icon-wrapper">
                    <img src={iconMessages} alt="" className="top-nav-icon" />
                    {unreadThreadCount > 0 && (
                      <span className="top-nav-message-badge">
                        {unreadThreadCount > 9 ? "9+" : unreadThreadCount}
                      </span>
                    )}
                  </div>
                  <span className="top-nav-label">Messages</span>
                </NavLink>

                <NavLink
                  to="/events"
                  className={({ isActive }) => `top-nav-item nav-events ${isActive ? "active" : ""}`}
                >
                  <div className="top-nav-icon-wrapper">
                    <img src={iconEvents} alt="" className="top-nav-icon" />
                  </div>
                  <span className="top-nav-label">Events</span>
                </NavLink>

                <NavLink
                  to="/profile"
                  className={`top-nav-item nav-profile ${profileRouteActive ? "active" : ""}`}
                >
                  <div className="top-nav-icon-wrapper">
                    <img src={iconProfile} alt="" className="top-nav-icon" />
                  </div>
                  <span className="top-nav-label">Profile</span>
                </NavLink>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {scannerMenuOpen && (
        <div ref={scannerMenuRef} className="top-nav-scanner-dropdown" style={scannerMenuStyle}>
          <button type="button" onClick={() => launchScannerModal("camera")}>
            Scan Card
          </button>
          <button type="button" onClick={() => launchScannerModal("upload")}>
            Upload Card
          </button>
        </div>
      )}

      {scannerModalMode && (
        <NavbarScannerModal
          launchMode={scannerModalMode}
          onRequestClose={() => setScannerModalMode(null)}
        />
      )}
    </>
  );
}
